import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { AnalyticsDataClickArea } from 'src/types/googleAnalyticsEvents';
import { useEventGeneratorClickCta } from 'src/analytics/useEventGeneratorClickCta';
import { useLanguagePairLinks } from 'src/hooks/useLanguagePairLinks';
import { Button } from 'src/components';
import { PageUrlMeta } from 'src/utils/getPageUrlMeta';
import { HeaderSourceLanguageDropdown } from './HeaderSourceLanguageDropdown';
import { Logo } from './Logo';
import { Sidebar } from './Sidebar';
import { DropdownSublist } from './DropdownSublist';
import * as S from './styles';
import { msgs } from './messages';

type HeaderProps = {
  alternateLinks?: PageUrlMeta['alternateLinks'];
};

export const Header: FC<HeaderProps> = ({ alternateLinks }) => {
  const intl = useIntl();
  const { signInHref, signUpHref, blogHref, languagesLinks, coursesLinks, phrasebookLinks } =
    useLanguagePairLinks();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const handleSidebarClick = useCallback(() => {
    setIsSidebarOpen(currentState => !currentState);
  }, [setIsSidebarOpen]);

  const eventGeneratorClickCta = useEventGeneratorClickCta({
    clickArea: AnalyticsDataClickArea.header,
  });

  return (
    <>
      <S.DevTools />
      <S.Header data-testid="header">
        <S.TopStripe>
          {!!alternateLinks?.length && (
            <HeaderSourceLanguageDropdown alternateLinks={alternateLinks} />
          )}
          <Button
            analyticsEvent="none"
            buttonSize="small"
            buttonType="secondary"
            disableMinWidth
            fullWidth="never"
            href={signInHref}
          >
            {intl.formatMessage(msgs.signIn)}
          </Button>
        </S.TopStripe>
        <S.MainStripe>
          <Logo />

          <S.Navigation>
            <S.SignIn href={signInHref}>{intl.formatMessage(msgs.signIn)}</S.SignIn>

            <S.NavigationList>
              {!!languagesLinks.length && (
                <S.NavigationListItem>
                  <DropdownSublist
                    buttonText={intl.formatMessage(msgs.languages)}
                    links={languagesLinks}
                  />
                </S.NavigationListItem>
              )}
              {!!coursesLinks.length && (
                <S.NavigationListItem>
                  <DropdownSublist
                    buttonText={intl.formatMessage(msgs.courses)}
                    links={coursesLinks}
                  />
                </S.NavigationListItem>
              )}
              {!!phrasebookLinks.length && (
                <S.NavigationListItem>
                  <DropdownSublist
                    buttonText={intl.formatMessage(msgs.phrasebooks)}
                    links={phrasebookLinks}
                  />
                </S.NavigationListItem>
              )}
              {blogHref && (
                <S.NavigationListItem>
                  <S.NavigationLink href={blogHref}>
                    {intl.formatMessage(msgs.blog)}
                  </S.NavigationLink>
                </S.NavigationListItem>
              )}
            </S.NavigationList>

            {signUpHref && (
              <S.SmallCtaButtonWrapper>
                <Button
                  analyticsEvent={eventGeneratorClickCta}
                  buttonSize="small"
                  buttonType="primary"
                  disableMinWidth
                  href={signUpHref}
                >
                  {intl.formatMessage(msgs.cta)}
                </Button>
              </S.SmallCtaButtonWrapper>
            )}

            <S.LargeCtaButtonWrapper>
              <Button
                analyticsEvent={eventGeneratorClickCta}
                buttonSize="large"
                buttonType="primary"
                disableMinWidth
                href={signUpHref}
              >
                {intl.formatMessage(msgs.cta)}
              </Button>
            </S.LargeCtaButtonWrapper>

            <S.MenuButtonWrapper>
              <S.MenuButton
                onClick={handleSidebarClick}
                aria-label={intl.formatMessage(msgs.menuAriaLabel)}
              >
                <S.MenuIcon />
              </S.MenuButton>
            </S.MenuButtonWrapper>
          </S.Navigation>
        </S.MainStripe>
      </S.Header>
      <Sidebar
        alternateLinks={alternateLinks}
        isOpen={isSidebarOpen}
        handleClose={handleSidebarClick}
      />
    </>
  );
};
